import { defineStore } from 'pinia'
import { useConnections } from './connections';
const axios = require('axios');

let attachmentId = 1;

export const usePostMaker = defineStore('post-maker', {
    state() {
        return {
            selectedConnections: {},
            attachments: [],

            // Post Maker page
            when: 'now',
            error: false,
            postNotCreated: false,
            scheduledFor: new Date(new Date().getTime() + (60000 * 10)), // +10 Minutes
            message: '',
            currentPostId: null,
            phase: 'edit', // validate, preview, post, complete,
            editing: false,
            draft: false,
            creationIds: [],
            repostOf: null,
            draftDateReset: false,
        }
    },

    getters: {
        // Get attachments for sending to endpoint (clean up)
        validAttachments() {
            let validAttachments = [];
            this.attachments.forEach((file) => {
                if(file.status !== 'ready' && file.attachment_type !== 'content') {
                    return {
                        error: 'attachments_not_ready'
                    };
                }

                // Clean up unnecessary keys
                if(file.attachment_type === 'upload') {
                    validAttachments.push({
                        conversion_id: file.conversion_id,
                        name: file.name,
                        key: file.key,
                        attachment_type: file.attachment_type,
                        type: file.type,
                        height: file.height,
                        width: file.width,
                        imgly: file.imgly,
                        id: file.id || null
                    });
                } else if(file.attachment_type === 'content') {
                    validAttachments.push(file);
                }
            });

            return validAttachments;
        },

        attachmentsWithTips() {
            let attachmentsWithTips = [];
            this.attachments.forEach((attachment) => {
                if (attachment.attachment_type === 'content' && attachment.tip !== null) {
                    attachmentsWithTips.push(attachment);
                }
            });

            return attachmentsWithTips;
        },

        // Get connections for sending to endpoint (clean up)
        validSelectedConnections() {
            let validSelectedConnections = {};
            for(let connectionId in this.selectedConnections) {
                // Clean up unnecessary keys
                let details = this.selectedConnections[connectionId];
                if(details.board_id) {
                    validSelectedConnections[connectionId] = {
                        board_id: details.board_id
                    };
                } else if(details.party_ids) {
                    validSelectedConnections[connectionId] = {
                        party_ids: details.party_ids,
                        post_title: details.post_title
                    };
                } else {
                    validSelectedConnections[connectionId] = {};
                }
            }

            return validSelectedConnections;
        },

        allAttachmentsUploaded() {
            let allUploaded = true;
            this.attachments.forEach((file) => {
                if(file.status !== 'ready' && file.attachment_type !== 'content') {
                    allUploaded = false;
                }
            });

            return allUploaded;
        },

        urlAttachments() {
            return this.attachments.filter((attachment) => {
                return attachment.attachment_type === 'content'
                    && attachment.content_type === 'url'
                    || attachment.content_type === 'streamable';
            });
        },

        previewableAttachments() {
            return this.attachments.filter((attachment) => {
                return attachment.attachment_type === 'upload'
                    || (
                        attachment.attachment_type === 'content'
                        && attachment.content_type === 'image'
                        || attachment.content_type === 'video'
                        || attachment.content_type === 'animation'
                        || attachment.content_type === 'streamable'
                        || attachment.content_type === 'url'
                    );
            });
        }
    },

    actions: {
        connectionSelected(id) {
            return this.selectedConnections.hasOwnProperty(id);
        },

        toggleConnection(id, other = false) {
            if(this.selectedConnections.hasOwnProperty(id)) {
                this.disconnect(id);
            } else {
                this.connect(id, other);
            }
        },

        connect(id, other = false) {
                this.selectedConnections[id] = {
                    status: 'posting',
                    error: null,
                    is_other_network: other
                };
        },

        disconnect(id) {
            delete this.selectedConnections[id];
        },

        setBoard(connectionId, boardId) {
            if(this.selectedConnections.hasOwnProperty(connectionId)) {
                this.selectedConnections[connectionId].board_id = boardId;
            }
        },

        setParties(connectionId, partyIds) {
            if(this.selectedConnections.hasOwnProperty(connectionId)) {
                //Eliminate stale parties
                let connections = useConnections();
                let tableConnection = connections.getConnectionById(connectionId);
                let validParties = tableConnection.parties.map(p => p.guid);
                let filteredPartyIds = partyIds.filter(partyId => validParties.includes(partyId));

                this.selectedConnections[connectionId].party_ids = filteredPartyIds;
            }
        },

        setPostTitle(connectionId, postTitle) {
            if(this.selectedConnections.hasOwnProperty(connectionId)) {
                this.selectedConnections[connectionId].post_title = postTitle;
            }
        },

        resetPostMaker() {
            this.$reset();
        },

        attachContentItem(content) {
            this.$patch((state) => {
                state.attachments.push({
                    attachment_type: 'content',
                    id: content.id,
                    thumbnail: content.thumbnail,
                    for_display: content.for_display,
                    for_publish: content.for_publish,
                    tip: content.tip,
                    name: content.title,
                    content_type: content.type,
                    details: content.details,
                    attachment_id: attachmentId,
                    links: content.links,
                    editable: content.editable,
                });
            });
            attachmentId++;
        },

        attachUserUpload(upload) {
            let length;

            this.$patch((state) => {
                length = state.attachments.push({
                    attachment_type: 'upload',
                    name: upload.name,
                    uuid: upload.upload ? upload.upload.uuid : null,
                    error: null,
                    thumbnail: upload.type.startsWith('image') ? 'images/generic-image.jpg' : 'images/generic-video.jpg',
                    for_display: upload.type.startsWith('image') ? 'images/generic-image.jpg' : 'images/generic-video.jpg',
                    for_publish: upload.type.startsWith('image') ? 'images/generic-image.jpg' : 'images/generic-video.jpg',
                    status: 'pending',
                    progress: 0,
                    height: upload.height,
                    width: upload.width,
                    dropzoneFile: upload,
                    type: upload.type,
                    attachment_id: attachmentId,
                    imgly: upload.imgly ?? null
                });
            });
            attachmentId++;
            return this.attachments[length - 1];
        },

        attachCreation(creation) {
            if (this.message.trim().length == 0) {
                this.message = creation.message;
            }

            this.creationIds.push(creation.id);

            if (creation.post_id !== null) {
                this.attachFromPost(creation.attachments);

                return;
            }

            for(let i = 0; i < creation.attachments.length; i++) {
                let currentUpload = creation.attachments[i];
                this.$patch((state) => {
                    state.attachments.push({
                        attachment_type: 'upload',
                        name: currentUpload.details.original_filename,
                        uuid: currentUpload.upload ? currentUpload.upload.uuid : null,
                        error: null,
                        thumbnail: currentUpload.thumbnail,
                        for_display: currentUpload.for_display,
                        for_publish: currentUpload.for_publish,
                        status: 'ready',
                        progress: 0,
                        height: currentUpload.details.height,
                        width: currentUpload.details.width,
                        dropzoneFile: {dataURL: currentUpload.source},
                        type: currentUpload.type,
                        attachment_id: attachmentId,
                        imgly: currentUpload.imgly ?? null,
                        id: currentUpload.id
                    });
                });
                attachmentId++;
            }
        },

        attachFromPost(attachments) {
            attachments.forEach(attachment => {
                let details = {
                    existing_attachment: true,
                    attachment_type: attachment.attachment_type,
                    id: attachment.id,
                    thumbnail: attachment.thumbnail,
                    for_display: attachment.for_display,
                    for_publish: attachment.for_publish,
                    tip: attachment.tip,
                    name: attachment.title ?? (attachment.details.original_filename ?? null),
                    type: attachment.type,
                    details: attachment.details,
                    attachment_id: attachmentId
                };

                if (attachment.attachment_type === 'content') {
                    details.tip = attachment.tip;
                    details.content_type = attachment.type;
                    details.editable = attachment.editable;
                }

                if (attachment.attachment_type === 'upload') {
                    details.status = 'ready';
                }

                this.$patch((state) => {
                    state.attachments.push(details);
                });

                attachmentId++;
            });
        },

        getAttachment(key, value) {
            return this.attachments.filter((file) => file[key] === value)[0];
        },

        removeAttachment(key, value) {
             const index = this.attachments.findIndex((file) => file[key] === value);
             return this.attachments.splice(index, 1)[0];
        },

        async imglyExportTemplate(template, scene, blob) {
            const imglyExportType = 'image/jpeg';
            let attachment = template;

            // If already attached, remove old attachment
            if (template.attachment_id) {
                attachment = this.removeAttachment('attachment_id', template.attachment_id);
            }

            let newAttachment = this.attachUserUpload({
                name: attachment.name ?? attachment.title,
                attachment_type: 'upload',
                type: imglyExportType,
                thumbnail: null,
                status: 'uploading',
                imgly: {
                    content_id: attachment.id ?? (attachment.imgly !== null ? attachment.imgly.content_id : null),
                    template: true,
                    scene: scene
                },
            });

            // Get policy to upload file
            let signResponse = await axios.post(
                '/api/uploads/sign',
                {
                    name: 'imgly_export.jpg',
                    type: imglyExportType,
                    filesize: blob.size
                }
            );

            // Build form and upload
            let formData = new FormData();
            let uploadKey = null;
            Object.keys(signResponse.data.form).forEach(key => {
                formData.append(key, signResponse.data.form[key]);
                if(key === 'key') {
                    uploadKey = signResponse.data.form[key];
                    newAttachment.key = uploadKey;
                }
            });
            formData.append('Content-type', imglyExportType);
            formData.append('file', blob);
            /*const uploadResponse = */await axios.post(signResponse.data.s3url, formData, {withCredentials: false});

            // Convert the upload
            newAttachment.status = 'converting';
            /*const conversionResponse = */await axios.post(
                '/api/uploads/convert',
                {
                    key: uploadKey,
                    type: imglyExportType
                },
            )

            // LATER: Error handling, catch and check responses(?)
            // Websockets takes things from here
        },

        // Edit a scheduled or draft post
        editPost(post, draft = false) {
            this.resetPostMaker();

            this.draft = draft;
            this.editing = true;
            this.currentPostId = post.id;
            this.message = post.message;

            if (this.draft && post.is_scheduled) {
                // Verify the scheduled date for the draft post
                let draftDate = new Date(post.scheduled_for);

                if (draftDate < this.scheduledFor) {
                    // Draft's scheduled date is in the past
                    this.draftDateReset = true;
                    this.when = 'now';
                } else {
                    this.scheduledFor = draftDate;
                    this.when = 'schedule';
                }
            }

            if (!this.draft) {
                // Assuming when editing a non-draft post it will still be scheduled
                this.scheduledFor = new Date(post.intended_for);
                this.when = 'schedule';
            }

            this.attachFromPost(post.attachments);

            // Verify the connections have loaded before trying to select them
            useConnections().ensureInitialized().then(() => {
                this.setConnections(post.connections);
            });
        },

        // Create a new post from an old post
        rePost(post) {
            this.resetPostMaker();
            this.repostOf = post.id;
            this.message = post.message;
            this.attachFromPost(post.attachments);

            // Verify the connections have loaded before trying to select them
            useConnections().ensureInitialized().then(() => {
                this.setConnections(post.connections);
            });
        },

        // Select the connections when loading an existing post or reposting
        setConnections(connections) {
            // Loop through connections, set selectedConnections
            connections.forEach(connection => {
                // Verify the existing connection is publishable before selecting it
                if (this.checkConnection(connection.id)) {
                    this.toggleConnection(connection.id, connection.is_other_network);

                    // If the post includes a Pinterest connection, we need to set the board ID as well
                    if (connection.post_details && connection.post_details.board_id) {
                        this.setBoard(connection.id, connection.post_details.board_id);
                    }

                    // If the post includes a Table connection, we need to set the party IDs as well
                    if (connection.post_details && connection.post_details.party_ids) {
                        this.setParties(connection.id, connection.post_details.party_ids);
                    }
                    // and post title
                    if (connection.post_details && connection.post_details.post_title) {
                        this.setPostTitle(connection.id, connection.post_details.post_title);
                    }
                }
            });
        },

        // Verify if a connection is publishable using the Connections store
        checkConnection(connectionId) {
            const connections = useConnections();

            // Checks against the publishableConnections array in the store
            return (connections.getConnectionById(connectionId) !== undefined);
        }
    }
});
